import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/utils/invokeEdgeFunction';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { CandidateForm, CandidateFormData } from '@/components/candidates/CandidateForm';
import { PositionSelectionStep } from '@/components/registration/PositionSelectionStep';
import { useToast } from '@/hooks/use-toast';
import { Sentry, isSentryEnabled } from '@/sentry';

// Utility to sanitize data similar to public registration
const sanitizeData = (data: any): any => {
  if (typeof data === 'string') {
    // eslint-disable-next-line no-control-regex
    return data.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '');
  }
  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }
  if (data && typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = sanitizeData(value);
    }
    return sanitized;
  }
  return data;
};

export default function CreateCandidatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Step management
  const [currentStep, setCurrentStep] = useState<'position-selection' | 'form'>('position-selection');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState<string | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [parsedResumeData, setParsedResumeData] = useState<any>(null);
  const [markDownText, setMarkDownText] = useState<string | null>(null);
  const [resumeParseStatus, setResumeParseStatus] = useState<'idle' | 'parsing' | 'success' | 'error'>('idle');
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prefillData, setPrefillData] = useState<Partial<CandidateFormData>>({});

  const normalizeInterviewerValue = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  };

  // Fetch job positions for the selection step
  const { data: jobPositions, isLoading: jobPositionsLoading } = useQuery({
    queryKey: ['job_positions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_positions')
        .select('id, role_name, job_type, interviewer')
        .eq('is_active', true)
        .order('role_name');
      if (error) throw error;
      return data;
    },
  });

  // Upload resume to R2 via edge function
  const uploadResumeToR2 = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    const result = await invokeEdgeFunction<{ url: string }>('upload-resume', {
      body: formData,
    });

    if (!result.url) {
      throw new Error('No URL returned from upload service');
    }

    return result.url;
  };

  // Parse resume and prefill form fields
  const parseResumeAndPrefill = async (file: File, position: string) => {
    try {
      setIsParsingResume(true);
      setResumeParseStatus('parsing');

      // Upload resume
      const uploadedUrl = await uploadResumeToR2(file);
      setResumeUrl(uploadedUrl);

      const parseResult = await invokeEdgeFunction<{ success: boolean; data: any; markdown: string | null }>('parse-resume-gemini', {
        body: {
          fileUrl: uploadedUrl,
          positionApplied: position,
        },
      });

      if (parseResult?.success && parseResult?.data) {
        const parsed = parseResult.data as any;
        const markdownContent = parseResult.markdown ?? parsed.resume_markdown ?? null;
        setParsedResumeData(parsed);
        setMarkDownText(markdownContent);
        setResumeParseStatus('success');

        const defaults: Partial<CandidateFormData> = {};
        if (parsed.full_name) defaults.full_name = parsed.full_name;
        if (parsed.email) defaults.email = parsed.email;
        if (parsed.phone) defaults.phone = parsed.phone;
        if (parsed.cgpa) {
          const cgpa = parseFloat(parsed.cgpa);
          if (!isNaN(cgpa)) defaults.cgpa = Math.round(cgpa * 100) / 100;
        }
        if (parsed.branch) defaults.branch = parsed.branch;
        if (parsed.qualification) defaults.qualification = parsed.qualification;
        if (parsed.latest_semester_score) {
          const sc = parseFloat(parsed.latest_semester_score);
          if (!isNaN(sc)) defaults.latest_semester_score = Math.round(sc * 100) / 100;
        }
        if (parsed.github_url) defaults.github_url = parsed.github_url;
        if (parsed.linkedin_url) defaults.linkedin_url = parsed.linkedin_url;
        if (parsed.key_skills?.length) defaults.key_skills = parsed.key_skills;
        if (parsed.technologies?.length) defaults.technologies = parsed.technologies;
        if (parsed.hobbies_interests?.length) defaults.hobbies_interests = parsed.hobbies_interests;
        if (parsed.projects_details?.length) defaults.projects_details = parsed.projects_details;
        if (parsed.project_links?.length) defaults.project_links = parsed.project_links;
        if (parsed.internships_experience?.length) defaults.internships_experience = parsed.internships_experience;
        if (parsed.certifications_awards?.length) defaults.certifications_awards = parsed.certifications_awards;

        defaults.position_applied = position;
        defaults.job_position_id = selectedJobPositionId || undefined;
        defaults.resume_url = uploadedUrl;
        defaults.resume_markdown = markdownContent;
        defaults.parsed_resume_data = parsed;

        setPrefillData(defaults);
        setCurrentStep('form');
        toast({
          title: 'Resume parsed successfully!',
          description: 'Form has been auto-filled. Please review before saving.',
        });
      } else {
        setResumeParseStatus('error');
        toast({
          title: 'Resume parsing failed',
          description: 'Retry parsing the resume or continue manually.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Resume parsing failed:', error);
      if (isSentryEnabled) Sentry.captureException(error);
      setResumeParseStatus('error');
      toast({
        title: 'Resume parsing failed',
        description: 'Retry parsing the resume or continue manually.',
        variant: 'destructive',
      });
    } finally {
      setIsParsingResume(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedPosition) {
      setResumeFile(file);
      setResumeParseStatus('idle');
      await parseResumeAndPrefill(file, selectedPosition);
    } else if (file && !selectedPosition) {
      toast({
        title: 'Position required',
        description: 'Please select a position before uploading the resume.',
        variant: 'destructive',
      });
    }
  };

  const handlePositionSelect = (roleName: string) => {
    setSelectedPosition(roleName);
    const job = jobPositions?.find((jp) => jp.role_name === roleName);
    setSelectedJobPositionId(job ? job.id : null);
  };

  const handleSkipResume = () => {
    if (selectedPosition) {
      setPrefillData({ position_applied: selectedPosition, job_position_id: selectedJobPositionId || undefined });
      setCurrentStep('form');
      toast({ title: 'Resume upload skipped', description: 'Fill the form manually.' });
    }
  };

  const handleRetryParsing = () => {
    if (resumeFile && selectedPosition) {
      parseResumeAndPrefill(resumeFile, selectedPosition);
    }
  };

  const handleSubmit = async (data: CandidateFormData) => {
    setIsSubmitting(true);
    try {
      const sanitized = sanitizeData(data);
      const candidateData = {
        full_name: sanitized.full_name,
        email: sanitized.email,
        phone: sanitized.phone,
        branch: sanitized.branch || '',
        qualification: sanitized.qualification || '',
        cgpa: sanitized.cgpa ? Math.round(sanitized.cgpa * 100) / 100 : null,
        college_university: sanitized.college_university || '',
        college_id: sanitized.college_id || null,
        job_position_id: sanitized.job_position_id || selectedJobPositionId || null,
        latest_semester_score: sanitized.latest_semester_score ? Math.round(sanitized.latest_semester_score * 100) / 100 : null,
        github_url: sanitized.github_url || '',
        linkedin_url: sanitized.linkedin_url || '',
        permanent_address: sanitized.permanent_address || '',
        permanent_city: sanitized.permanent_city || '',
        current_address: sanitized.current_address || '',
        current_city: sanitized.current_city || '',
        position_applied: selectedPosition || sanitized.position_applied || '',
        hobbies_interests: sanitized.hobbies_interests?.filter((v: string) => v.trim()) || [],
        key_skills: sanitized.key_skills?.filter((v: string) => v.trim()) || [],
        technologies: sanitized.technologies?.filter((v: string) => v.trim()) || [],
        status: sanitized.status,
        current_round: sanitized.current_round,
        projects_details: sanitized.projects_details?.filter((v: string) => v.trim()) || [],
        project_links: sanitized.project_links?.filter((v: string) => v.trim()) || [],
        certifications_awards: sanitized.certifications_awards?.filter((v: string) => v.trim()) || [],
        event_participations: sanitized.event_participations?.filter((v: string) => v.trim()) || [],
        internships_experience: sanitized.internships_experience?.filter((v: string) => v.trim()) || [],
        resume_url: resumeUrl,
        resume_markdown: sanitizeData(markDownText),
        parsed_resume_data: sanitizeData(parsedResumeData),
        interviewer: normalizeInterviewerValue(sanitized.interviewer).length > 0
          ? normalizeInterviewerValue(sanitized.interviewer)
          : (() => {
              const fallbackInterviewers = normalizeInterviewerValue(
                jobPositions?.find(j => j.id === (sanitized.job_position_id || selectedJobPositionId))?.interviewer,
              );

              return fallbackInterviewers.length > 0 ? fallbackInterviewers : null;
            })(),
        joining_date: sanitized.joining_date || null,
        submitted_at: new Date().toISOString(),
      };

      const { data: candidate, error } = await supabase
        .from('candidates')
        .insert(candidateData as never)
        .select()
        .single();

      if (error) throw error;

      supabase.functions
        .invoke('evaluate-cv-gemini', {
          body: {
            candidateId: candidate.id,
            positionApplied: candidateData.position_applied,
          },
        })
        .catch((evaluationError) => {
          console.error('CV evaluation error:', evaluationError);
          if (isSentryEnabled) Sentry.captureException(evaluationError);
        });

      toast({
        title: 'Candidate Created',
        description: 'The candidate has been created. CV evaluation will run in the background.',
      });

      navigate('/admin/candidates');
    } catch (error) {
      console.error('Error creating candidate:', error);
      if (isSentryEnabled) Sentry.captureException(error);
      const message =
        (error as any)?.message ||
        (error as any)?.error?.message ||
        'Failed to create candidate. Please try again.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/candidates')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidates
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Create New Candidate</h1>
          <p className="text-muted-foreground">Add a new candidate to the system</p>
        </div>
      </div>

      {currentStep === 'position-selection' && (
        <PositionSelectionStep
          jobPositions={jobPositions || []}
          isLoadingJobPositions={jobPositionsLoading}
          selectedPosition={selectedPosition}
          onPositionSelect={handlePositionSelect}
          resumeFile={resumeFile}
          parseStatus={resumeParseStatus}
          isVerifyingCaptcha={false}
          recaptchaLoaded={true}
          onFileChange={handleFileUpload}
          onRetry={handleRetryParsing}
          onContinue={handleSkipResume}
          canContinue={!!selectedPosition}
        />
      )}

      {currentStep === 'form' && (
        <Card>
          <CardHeader>
            <CardTitle>Candidate Information</CardTitle>
            <CardDescription>
              Fill in the details for the new candidate. CV evaluation will be performed automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CandidateForm initialData={prefillData} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
